import { EditOutlined } from "@mui/icons-material";
import { Button } from "@mui/material";
import { isArray, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import DateInput from "../../../components/inputs/DateInput";
import DollarInput from "../../../components/inputs/DollarInput";
import PercentInput from "../../../components/inputs/PercentInput";
import SelectInput from "../../../components/inputs/SelectInput";
import TextInput from "../../../components/inputs/TextInput";
import { FormField } from "../../../components/forms/FormPrimitives";
import QuickEditDialog from "../../../components/forms/QuickEditDialog";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { PipelineProvider } from "../../../pipelines/PipelineContext";
import {
  loanPipelineClear,
  loanPipelineUpdateField,
} from "../../../redux/actions/loanPipelineActions";

const PipelineScope = ({ pipelineKey, children }) =>
  pipelineKey ? (
    <PipelineProvider pipeline={pipelineKey}>{children}</PipelineProvider>
  ) : (
    children
  );

const CellRendererEditableLoanField = (params) => {
  const [show, setShow] = useState(null);
  // console.log("params", params?.data?.loanName, params);
  const { data } = params;
  const loanData = useMemo(() => params?.data || {}, [params]);
  const field = useMemo(() => params?.colDef?.field, [params]);

  const fieldLabel = useMemo(() => params?.colDef?.headerName, [params]);
  const fieldType = useMemo(() => {
    if (params?.colDef?.type) {
      return params?.colDef?.type;
    }

    if (isArray(params?.colDef?.cellEditorParams?.values)) {
      return "select";
    }
    return "text";
  }, [params]);

  const fieldSelectOptions = useMemo(
    () => params?.colDef?.cellEditorParams?.values || [],
    [params],
  );
  return (
    <>
      <Button
        sx={{ fontSize: "inherit", fontWeight: 400, color: "inherit" }}
        variant="text"
        onClick={() => setShow(params)}
      >
        {params?.valueFormatted || params?.value}
        <EditOutlined
          sx={{ fontSize: "16px", ml: 0.5 }}
          color="primary"
          className="thin-9 ag-editable-edit-icon"
        />
      </Button>
      {/* Mixed boards (Full Pipeline) tag each row with its pipeline, so an
          acquisition row saves through /loan_acquisitions/* rather than the
          underwriting router. Untagged rows keep the surrounding context. */}
      <PipelineScope pipelineKey={loanData?._pipelineKey}>
        <UpdateLoanDataModal
          show={show}
          loanId={data?._id}
          loanData={loanData}
          field={field}
          fieldLabel={fieldLabel}
          fieldType={fieldType}
          fieldSelectOptions={fieldSelectOptions}
          onClose={() => setShow(null)}
        />
      </PipelineScope>
    </>
  );
};

const UpdateLoanDataModal = ({
  show,
  loanData,
  field,
  fieldLabel,
  fieldType,
  fieldSelectOptions,
  onClose,
}) => {
  const { loading, updateLoanData, updateMilestoneStatus } =
    useUnderwritingHook();
  const dispatch = useDispatch();
  const [newValue, setNewValue] = useState(null);

  const currentValue = useMemo(() => loanData?.[field], [loanData, field]);

  const loanName = useMemo(
    () => loanData?.loanName || loanData?.loanNumber,
    [loanData],
  );

  const handleOnClose = useCallback(() => {
    setNewValue(null);
    onClose();
  }, [onClose]);

  const onUpdateClick = () => {
    const loanId = loanData?._id;

    if (field === "loanStatus") {
      updateMilestoneStatus({
        loanId: loanId,
        loanStatus: newValue,
        onSuccessFn: (res) => {
          dispatch(loanPipelineClear());
          handleOnClose();
        },
      });
      return;
    }

    updateLoanData({
      loanId: loanId,
      data: { [field]: newValue },
      onSuccessFn: (res) => {
        // The update response is the full (differently-shaped) loan doc, so we
        // don't replace the pipeline row with it. Instead update only the edited
        // field: in Redux (the LO pipeline's source of truth) and on the grid
        // node directly (immediate refresh, and covers grids backed by local
        // state like the pre-close/closing tables).
        dispatch(loanPipelineUpdateField({ loanId, field, value: newValue }));
        const node = show?.node;
        if (node?.setData) {
          node.setData({ ...node.data, [field]: newValue });
        }
        handleOnClose();
      },
    });
  };

  useEffect(() => {
    setNewValue(loanData?.[field] || null);
  }, [loanData, field]);

  if (isNil(show)) {
    return null;
  }

  // One control, picked by the column's type. The field's label is hidden by
  // the quick-edit shell (the title carries it), so this is the input alone.
  const control =
    fieldType === "dollar" ? (
      <DollarInput value={newValue} onChange={setNewValue} />
    ) : fieldType === "percent" ? (
      <PercentInput value={newValue} onChange={setNewValue} />
    ) : fieldType === "date" ? (
      <DateInput value={newValue} onChange={setNewValue} />
    ) : fieldType === "select" ? (
      <SelectInput
        value={newValue}
        onChange={setNewValue}
        options={fieldSelectOptions}
      />
    ) : (
      <TextInput value={newValue} onChange={setNewValue} />
    );

  return (
    // The quick-edit modal (FORM_STYLE_GUIDE.md §12c): one field, so the TITLE
    // names it and the line under it says which loan — this opens from a
    // pipeline row, where "Update Loan" told you neither.
    <QuickEditDialog
      open={!isNil(show)}
      title={fieldLabel}
      description={
        loanData?.loanNumber ? `#${loanData.loanNumber} · ${loanName}` : loanName
      }
      onClose={handleOnClose}
      onSave={onUpdateClick}
      saving={loading}
      canSave={currentValue !== newValue}
      // A picker and a select both take Enter themselves.
      enterHint={fieldType !== "date" && fieldType !== "select"}
    >
      <FormField label={fieldLabel}>{control}</FormField>
    </QuickEditDialog>
  );
};

export default CellRendererEditableLoanField;
